// src/utils/adminListsApi.js
//
// Review of the restaurant lists and the influencers (backend
// routes/adminLists.ts, admins only). Errors carry the backend's code in
// err.message (REASON_REQUIRED, INVALID_URL, EMPTY_LIST, ...).
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';
const BASE = `${API_BASE_URL}/admin/lists`;

async function call(method, path, token, body) {
  const r = await fetch(`${BASE}${path}`, {
    method,
    headers: { Authorization: `Bearer ${token}`, ...(body ? { 'Content-Type': 'application/json' } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) {
    const err = new Error(data.error || `HTTP_${r.status}`);
    err.status = r.status;
    throw err;
  }
  return data;
}

export const reviewQueue = (token) => call('GET', '/review', token);
export const reportedLists = (token) => call('GET', '/reported', token);
export const allLists = (token, { status = '', q = '' } = {}) =>
  call('GET', `/all?status=${encodeURIComponent(status)}&q=${encodeURIComponent(q)}`, token);
export const approveList = (id, token) => call('POST', `/${id}/approve`, token, {});
export const rejectList = (id, reason, token) => call('POST', `/${id}/reject`, token, { reason });
export const hideList = (id, token) => call('POST', `/${id}/hide`, token, {});
export const restoreList = (id, token) => call('POST', `/${id}/restore`, token, {});
export const deleteList = (id, token) => call('DELETE', `/${id}`, token);
// Comments under the lists (routes/listSocial.ts): the reported or hidden ones.
export const reportedComments = (token) => call('GET', '/comments/reported', token);
export const hideComment = (id, token) => call('POST', `/comments/${id}/hide`, token, {});
export const restoreComment = (id, token) => call('POST', `/comments/${id}/restore`, token, {});
// Photos under the lists' places: shown only once approved here.
export const photoQueue = (token) => call('GET', '/photos/review', token);
export const reportedPhotos = (token) => call('GET', '/photos/reported', token);
export const approvePhoto = (id, token) => call('POST', `/photos/${id}/approve`, token, {});
export const rejectPhoto = (id, reason, token) => call('POST', `/photos/${id}/reject`, token, { reason });
export const hidePhoto = (id, token) => call('POST', `/photos/${id}/hide`, token, {});
export const restorePhoto = (id, token) => call('POST', `/photos/${id}/restore`, token, {});
/** A photo of any status: fetched with the token (an <img> can't send it), as an object URL. */
export async function adminPhotoUrl(id, token) {
  const r = await fetch(`${BASE}/photos/${Number(id)}.jpg`, { headers: { Authorization: `Bearer ${token}` } });
  if (!r.ok) throw new Error(`HTTP_${r.status}`);
  return URL.createObjectURL(await r.blob());
}
/** A profile photo waiting for review, fetched the same way. */
export async function adminProfilePhotoUrl(id, token) {
  const r = await fetch(`${BASE}/profiles/${Number(id)}/avatar.jpg`, { headers: { Authorization: `Bearer ${token}` } });
  if (!r.ok) throw new Error(`HTTP_${r.status}`);
  return URL.createObjectURL(await r.blob());
}
export const profileQueue = (token) => call('GET', '/profiles/review', token);
export const approveProfile = (id, token) => call('POST', `/profiles/${id}/approve`, token, {});
export const rejectProfile = (id, reason, token) => call('POST', `/profiles/${id}/reject`, token, { reason });
export const pseudoQueue = (token) => call('GET', '/pseudos/review', token);
export const approvePseudo = (id, token) => call('POST', `/pseudos/${id}/approve`, token, {});
export const rejectPseudo = (id, token) => call('POST', `/pseudos/${id}/reject`, token, {});

export const listInfluencers = (token) => call('GET', '/influencers', token);
export const createInfluencer = (data, token) => call('POST', '/influencers', token, data);
export const updateInfluencer = (id, data, token) => call('PUT', `/influencers/${id}`, token, data);
export const addInfluencerList = (id, list, token) => call('POST', `/influencers/${id}/lists`, token, list);

const ERRORS = {
  REASON_REQUIRED: 'Indiquez un motif de refus.',
  NOTHING_TO_APPROVE: 'Rien à valider sur cette liste.',
  NOTHING_TO_REJECT: 'Rien à refuser sur cette liste.',
  INVALID_URL: 'Lien invalide : il doit commencer par http:// ou https://.',
  NAME_REQUIRED: 'Le nom est obligatoire.',
  EMPTY_LIST: 'La liste ne contient aucun lieu.',
  INVALID_ITEM: 'Un lieu n’a pas de nom ou de position valide.',
  TOO_MANY_ITEMS: 'Trop de lieux (50 maximum).',
  LIST_NOT_FOUND: 'Liste introuvable.',
  COMMENT_NOT_FOUND: 'Commentaire introuvable.',
  PHOTO_NOT_FOUND: 'Photo introuvable.',
  NOTHING_TO_REVIEW: 'Ce pseudo n’attend plus de validation.',
  PSEUDO_TAKEN: 'Un autre utilisateur a déjà ce pseudo : refusez-le.',
  AUTHOR_NOT_FOUND: 'Utilisateur introuvable.',
};
export const listErrorText = (e) => ERRORS[e?.message] || 'Erreur, réessayez.';
